import { inject, Injectable } from '@angular/core';
import { Store } from '@sama/app-state';
import { isDefined } from '@sama/js-core';
import { isEmpty, isUndefined, pickBy } from 'lodash';
import { filter } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { ObjectClassService } from '../../../../core/shared/misc-services/object-class.service';
import { AnnotationState } from '../../../../cross-team-shared/annotation/shared/state/annotation-state';
import { isShapeState } from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { Attributes } from '../../../../team-apis/core/taxonomy';
import { ShapeType } from '../../../../team-apis/otterdam/answers/shape-typings';

@Injectable()
export class AvailableAttributeService {
  private objectClassService = inject(ObjectClassService);

  getAttributes(annotation: AnnotationState) {
    const className = this.objectClassService.getClassName(annotation);
    const attributes = this.objectClassService.getClassAttributes(className);

    if (!attributes) {
      return undefined;
    }
    if (isShapeState(annotation)) {
      return filterAttributesByShapeType(attributes, annotation.type);
    }

    return filterAttributesForObjectsOnly(attributes);
  }

  getAnnotationAttributes$(annotationStore: Store<AnnotationState>) {
    return annotationStore.$.pipe(
      // The annotation store is undefined when annotations are deleted
      filter(isDefined),
      distinctUntilChanged(
        (previous, current) =>
          this.objectClassService.getClassName(previous) ===
          this.objectClassService.getClassName(current),
      ),
      map((annotation) => this.getAttributes(annotation)),
    );
  }
}

function filterAttributesForObjectsOnly(attributes: Attributes): Attributes {
  return pickBy(attributes, (attribute) => {
    if (isUndefined(attribute)) {
      return false;
    }

    return isUndefined(attribute.shapes) || !attribute.shapes.isShapeAttribute;
  });
}

function filterAttributesByShapeType(
  attributes: Attributes,
  shapeType: ShapeType,
): Attributes {
  return pickBy(attributes, (attribute) => {
    if (
      isUndefined(attribute) ||
      isUndefined(attribute.shapes) ||
      !attribute.shapes.isShapeAttribute
    ) {
      return false;
    }

    return (
      isEmpty(attribute.shapes.shapeTypes) ||
      attribute.shapes.shapeTypes.includes(shapeType)
    );
  });
}
